import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense, useMemo } from "react";
import { LoadingProvider } from "./contexts/LoadingContext";
import { CenteredLoader, ErrorBoundary } from "./components";
import ApiTest from "./components/ApiTest";
import logger from "./utils/logger.js";

// Lazy loading des composants pour améliorer les performances
// Pages publiques
const NavBar = lazy(() => import("./navBar/index.jsx"));
const Home = lazy(() => import("./Home/index.jsx"));
const Devis = lazy(() => import("./Devis/index.jsx"));
const Contact = lazy(() => import("./Contact/index.jsx"));
const MentionsLegales = lazy(() => import("./MentionsLegales/index.jsx"));
const PolitiqueConfidentialite = lazy(() => import("./PolitiqueConfidentialite/index.jsx"));
const CookiesPage = lazy(() => import("./Cookies/index.jsx"));
const ConditionsUtilisation = lazy(() => import("./ConditionsUtilisation/index.jsx"));
const Connexion = lazy(() => import("./Connexion/index.jsx"));
const Footer = lazy(() => import("./footer/index.jsx"));

// Pages du dashboard
const TableauDeBord = lazy(() => import("./Dashboard/TableauDeBord/index.jsx"));
const NotreEntreprise = lazy(() => import("./Dashboard/notreEntreprise/index.jsx"));
const Clients = lazy(() => import("./Dashboard/Clients/index.jsx"));
const ListeClients = lazy(() => import("./Dashboard/Clients/ListeClients.jsx"));
const NosDevis = lazy(() => import("./Dashboard/NosDevis/index.jsx"));
const ListeCommissionnaire = lazy(() => import("./Dashboard/listeCommissionnaire/index.jsx"));
const Factures = lazy(() => import("./Dashboard/Factures/index.jsx"));
const Transport = lazy(() => import("./Dashboard/Transport/index.jsx"));
const Messagerie = lazy(() => import("./Dashboard/Messagerie/index.jsx"));
const Utilisateurs = lazy(() => import("./Dashboard/Utilisateurs/index.jsx"));
const ConditionsTransport = lazy(() => import("./Dashboard/ConditionsTransport/index.jsx"));
const SurveillanceAPI = lazy(() => import("./Dashboard/SurveillanceAPI/index.jsx"));
const CacheManager = lazy(() => import("./Dashboard/CacheManager/index.jsx"));

// Composants utilitaires
const PrivateRoute = lazy(() => import("./PrivateRoute"));
const NotFound = lazy(() => import("./NotFound"));

// Composant de chargement pour le lazy loading - maintenant géré par le système global
const LoadingComponent = () => null;

export default function App() {
  const location = useLocation();
  
  // Effet pour remonter en haut de la page à chaque changement de route
  useEffect(() => {
    window.scrollTo(0, 0);
    logger.route(location.pathname, location.pathname);
  }, [location.pathname]);

  // Définir les routes du dashboard avec useMemo pour éviter les recalculs
  const dashboardRoutes = useMemo(() => [
    "/dashboard/full",
    "/NotreEntreprise",
    "/clients",
    "/listeClients",
    "/listeCommissionnaire",
    "/nosDevis",
    "/factures",
    "/transport",
    "/messagerie",
    "/utilisateurs",
    "/dashboard/conditions-transport",
    "/dashboard/surveillance-api",
    "/dashboard/cache-manager",
  ], []);

  // Vérifier si l'URL actuelle correspond à une page du Dashboard
  const isDashboardPage = useMemo(() => 
    dashboardRoutes.includes(location.pathname), 
    [location.pathname, dashboardRoutes]
  );

  return (
    <ErrorBoundary>
      <LoadingProvider>
        <CenteredLoader />
        
        {/* Barre de navigation - masquée sur les pages du dashboard */}
        {!isDashboardPage && (
          <Suspense fallback={<LoadingComponent />}>
            <NavBar />
          </Suspense>
        )}

        {/* Routes */}
        <Suspense fallback={<LoadingComponent />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/devis" element={<Devis />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/conditions-utilisation" element={<ConditionsUtilisation />} />
            <Route path="/connexion" element={<Connexion />} />
            <Route path="/api-test" element={<ApiTest />} />

            {/* Routes protégées du dashboard */}
            <Route element={<PrivateRoute />}>
              <Route path="/dashboard/full" element={<TableauDeBord />} />
              <Route path="/NotreEntreprise" element={<NotreEntreprise />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/listeClients" element={<ListeClients />} />
              <Route path="/listeCommissionnaire" element={<ListeCommissionnaire />} />
              <Route path="/nosDevis" element={<NosDevis />} />
              <Route path="/factures" element={<Factures />} />
              <Route path="/transport" element={<Transport />} />
              <Route path="/messagerie" element={<Messagerie />} />
              <Route path="/utilisateurs" element={<Utilisateurs />} />
              <Route path="/dashboard/conditions-transport" element={<ConditionsTransport />} />
              <Route path="/dashboard/surveillance-api" element={<SurveillanceAPI />} />
              <Route path="/dashboard/cache-manager" element={<CacheManager />} />
            </Route>

            {/* Route 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        {/* Footer - masqué sur les pages du dashboard */}
        {!isDashboardPage && (
          <Suspense fallback={<LoadingComponent />}>
            <Footer />
          </Suspense>
        )}

        {/* Panneau de debug - visible en mode développement */}
        {/* {process.env.NODE_ENV === 'development' && <DebugPanel />} */}
      </LoadingProvider>
    </ErrorBoundary>
  );
}
